import { FormName } from '@core/data-access/models/form/form.model';
import {
  TicketAssignmentStoreState,
  TicketHolderForm,
  TicketHolderQuestionnaireDynamicFields
} from '@core/features/ticket-assignment/models/ticket-assignment-store.model';
import { TariffUuids } from '@products/models/tariff.model';
import { difference, head, includes, isEmpty, keys, omit, times, zipObject } from 'lodash';

export const setTicketHolderFormsAndQuestionnaireDynamicFieldsOnAddedFeature = (
  state: TicketAssignmentStoreState,
  tariffUuids: TariffUuids,
  ticketHolderQuestionnaireDynamicFields: TicketHolderQuestionnaireDynamicFields
) => {
  if (isEmpty(tariffUuids)) {
    return state;
  }

  const { forms } = state;

  const currentTicketHolderTariffUuids = keys(forms.ticketHolder);
  const tariffUuidsToAdd = difference(tariffUuids, currentTicketHolderTariffUuids);

  const selectedTariffUuid = state.selectedTariffUuid || head(tariffUuidsToAdd);

  const updatedDynamicFields = {
    ...state.dynamicFields,
    [FormName.TicketHolderQuestionnaire]: {
      ...state.dynamicFields[FormName.TicketHolderQuestionnaire],
      ...ticketHolderQuestionnaireDynamicFields
    }
  };

  if (isEmpty(tariffUuidsToAdd)) {
    return {
      ...state,
      selectedTariffUuid,
      dynamicFields: updatedDynamicFields
    };
  }

  const ticketHolderForm: TicketHolderForm = {
    person: null,
    formFields: {}
  };

  const updatedTicketHolderForms = zipObject(
    tariffUuidsToAdd,
    times(tariffUuidsToAdd.length, () => ticketHolderForm)
  );

  return {
    ...state,
    selectedTariffUuid,
    dynamicFields: updatedDynamicFields,
    forms: {
      ...forms,
      [FormName.TicketHolder]: updatedTicketHolderForms
    }
  };
};

export const addTicketHolderFormsAndQuestionnaireDynamicFields = (
  state: TicketAssignmentStoreState,
  tariffUuids: TariffUuids,
  ticketHolderQuestionnaireDynamicFields: TicketHolderQuestionnaireDynamicFields
) => {
  if (isEmpty(tariffUuids)) {
    return state;
  }

  const { forms } = state;

  const selectedTariffUuid = state.selectedTariffUuid || head(tariffUuids);

  const ticketHolderForm: TicketHolderForm = {
    person: null,
    formFields: {}
  };

  const addedTicketHolderForms = zipObject(
    tariffUuids,
    times(tariffUuids.length, () => ticketHolderForm)
  );

  return {
    ...state,
    selectedTariffUuid,
    dynamicFields: {
      ...state.dynamicFields,
      [FormName.TicketHolderQuestionnaire]: {
        ...state.dynamicFields[FormName.TicketHolderQuestionnaire],
        ...ticketHolderQuestionnaireDynamicFields
      }
    },
    forms: {
      ...forms,
      [FormName.TicketHolder]: {
        ...forms.ticketHolder,
        ...addedTicketHolderForms
      }
    }
  };
};

export const removeTicketHolderForms = (state: TicketAssignmentStoreState, tariffUuids: TariffUuids) => {
  if (isEmpty(tariffUuids)) {
    return state;
  }

  const { forms } = state;

  let selectedTariffUuid = state.selectedTariffUuid;

  if (includes(tariffUuids, state.selectedTariffUuid)) {
    const currentTariffUuids = keys(forms.ticketHolder);
    const notRemovedTariffUUids = difference(currentTariffUuids, tariffUuids);

    selectedTariffUuid = head(notRemovedTariffUUids);
  }

  return {
    ...state,
    selectedTariffUuid,
    forms: {
      ...forms,
      [FormName.TicketHolder]: omit(forms[FormName.TicketHolder], tariffUuids),
      [FormName.TicketHolderQuestionnaire]: omit(forms[FormName.TicketHolderQuestionnaire], tariffUuids)
    }
  };
};
